import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Target, Save, Check, ChevronsUpDown } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";

const ROLE_LABELS = { sales: "Technical Sales", qualification: "Lead Qualification", partner: "Head of Partner" };

export default function ObiettiviProspect() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [rows, setRows] = useState({});
  const [vis, setVis] = useState({});
  const [allProv, setAllProv] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/settings/prospect-targets").then((r) => {
    const c = r.data;
    setCfg(c);
    setDefaults({ ...c.defaults });
    const map = {};
    (c.user_list || []).forEach((u) => {
      const ex = (c.users || {})[u.id];
      map[u.id] = {
        active: !!(ex && ex.active),
        targets: { ...c.defaults, ...((ex && ex.targets) || {}) },
      };
    });
    setRows(map);
    const vmap = {};
    (c.user_list || []).forEach((u) => {
      const ex = (c.visibility || {})[u.id];
      vmap[u.id] = ((ex && ex.provinces) || []);
    });
    setVis(vmap);
    setAllProv(c.all_provinces || []);
  }).catch(() => setCfg(false));
  const loadLead = () => {};
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const visibility = {};
      Object.entries(vis).forEach(([uid, arr]) => {
        const provinces = (arr || []).map((s) => String(s).trim().toUpperCase()).filter(Boolean);
        if (provinces.length) visibility[uid] = { provinces };
      });
      await api.put("/settings/prospect-targets", { defaults, users: rows, visibility });
      toast.success("Obiettivi salvati");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (cfg === null) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (cfg === false) return <p className="text-sm text-muted-foreground p-8">Impossibile caricare gli obiettivi.</p>;

  const keys = cfg.keys || [];
  const labels = cfg.labels || {};

  const setDef = (k, v) => setDefaults((p) => ({ ...p, [k]: v === "" ? "" : Math.max(0, parseInt(v, 10) || 0) }));
  const setRow = (id, patch) => setRows((p) => ({ ...p, [id]: { ...p[id], ...patch } }));
  const setRowTarget = (id, k, v) => setRows((p) => ({ ...p, [id]: { ...p[id], targets: { ...p[id].targets, [k]: v === "" ? "" : Math.max(0, parseInt(v, 10) || 0) } } }));

  return (
    <div data-testid="obiettivi-prospect-page">
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          <div><h1 className="text-xl font-heading font-bold">Obiettivi Sales</h1><p className="text-xs text-muted-foreground">Obiettivo giornaliero di azioni totali (Lead + Prospect) per commerciale</p></div>
        </div>
        <Button onClick={save} disabled={saving} className="font-semibold" data-testid="obp-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
        </Button>
      </header>

      <div className="p-4 lg:p-8 space-y-6">
        <Card className="rounded border-border p-4 lg:p-5" data-testid="obp-defaults">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Target di default (giornaliero)</div>
          <p className="text-xs text-muted-foreground mb-4">Azioni totali giornaliere (Lead + Prospect) usate quando un commerciale è attivo ma non ha un target personalizzato. Nella dashboard "Attività" il target del periodo = valore giornaliero × giorni lavorativi.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            {keys.map((k) => (
              <div key={k}>
                <label className="text-[11px] text-muted-foreground leading-tight block mb-1">{labels[k]}</label>
                <Input type="number" min={0} value={defaults[k] ?? ""} onChange={(e) => setDef(k, e.target.value)} data-testid={`obp-default-${k}`} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded border-border overflow-x-auto" data-testid="obp-users">
          <div className="p-4 border-b border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Commerciali · obiettivi personalizzati</div>
            <p className="text-xs text-muted-foreground mt-1">Attiva chi svolge attività. Conta ogni azione registrata (chiamata, meet, sopralluogo, consulenza, preventivo, trattativa, contratto sui Lead + azioni sui Prospect). Le "note" non contano. Gli obiettivi degli utenti <b>attivi</b> vengono sommati nella vista "Tutto il team".</p>
          </div>
          {(cfg.user_list || []).length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Nessun utente con accesso ai prospect.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/40 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                  <th className="text-left font-semibold px-3 py-2 sticky left-0 bg-secondary/40">Commerciale</th>
                  <th className="text-center font-semibold px-3 py-2">Attivo</th>
                  {keys.map((k) => <th key={k} className="text-center font-semibold px-2 py-2 min-w-[92px]">{labels[k]}</th>)}
                </tr>
              </thead>
              <tbody>
                {(cfg.user_list || []).map((u) => {
                  const row = rows[u.id] || { active: false, targets: {} };
                  return (
                    <tr key={u.id} className="border-t border-border" data-testid={`obp-row-${u.id}`}>
                      <td className="px-3 py-2 sticky left-0 bg-background">
                        <div className="font-medium">{u.name}</div>
                        <div className="text-[10px] text-muted-foreground">{ROLE_LABELS[u.role] || u.role}</div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => setRow(u.id, { active: !row.active })} data-testid={`obp-active-${u.id}`}
                          className={`inline-flex h-6 w-11 items-center rounded-full transition-colors ${row.active ? "bg-emerald-500" : "bg-secondary"}`}>
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${row.active ? "translate-x-5" : "translate-x-0.5"}`} />
                        </button>
                      </td>
                      {keys.map((k) => (
                        <td key={k} className="px-2 py-2">
                          <Input type="number" min={0} disabled={!row.active} value={row.targets?.[k] ?? ""} onChange={(e) => setRowTarget(u.id, k, e.target.value)} data-testid={`obp-${u.id}-${k}`} className={`h-8 text-center ${!row.active ? "opacity-40" : ""}`} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="rounded border-border" data-testid="obp-visibility">
          <div className="p-4 border-b border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Visibilità prospect per provincia</div>
            <p className="text-xs text-muted-foreground mt-1">Di base ogni utente vede <b>tutti</b> i prospect (Dashboard, Pipeline, ovunque). Selezionando una o più province l'utente vedrà <b>solo</b> quelle. Nessuna selezione = tutte. L'admin vede sempre tutto.</p>
            {allProv.length > 0 && <p className="text-[11px] text-muted-foreground mt-2">Province disponibili: <span className="font-mono">{allProv.join(", ")}</span></p>}
          </div>
          {(cfg.user_list || []).length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Nessun utente con accesso ai prospect.</p>
          ) : (
            <div className="divide-y divide-border">
              {(cfg.user_list || []).map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid={`obp-vis-row-${u.id}`}>
                  <div className="w-48 shrink-0">
                    <div className="font-medium text-sm">{u.name}</div>
                    <div className="text-[10px] text-muted-foreground">{ROLE_LABELS[u.role] || u.role}</div>
                  </div>
                  <ProvinceMultiSelect options={allProv} value={vis[u.id] || []} onChange={(arr) => setVis((p) => ({ ...p, [u.id]: arr }))} testid={`obp-vis-${u.id}`} />
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5 text-emerald-500" /> Gli obiettivi guidano la tabella "Questa settimana" e lo "Storico settimane" della Dashboard Prospect (per singolo commerciale o somma del team).
        </div>
      </div>
    </div>
  );
}

function ProvinceMultiSelect({ options, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (p) => {
    const set = new Set(value);
    if (set.has(p)) set.delete(p); else set.add(p);
    onChange(Array.from(set).sort());
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="flex-1 justify-between font-normal h-auto min-h-9 py-1.5" data-testid={testid}>
          <span className="flex flex-wrap gap-1 items-center text-left">
            {(!value || value.length === 0)
              ? <span className="text-muted-foreground">Tutte le province (nessun limite)</span>
              : value.map((p) => <Badge key={p} variant="secondary" className="text-[11px] font-mono">{p}</Badge>)}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Cerca provincia…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>Nessuna provincia</CommandEmpty>
            <CommandGroup>
              {(options || []).map((p) => {
                const sel = (value || []).includes(p);
                return (
                  <CommandItem key={p} value={p} onSelect={() => toggle(p)} data-testid={`${testid}-opt-${p}`}>
                    <Checkbox checked={sel} className="mr-2 pointer-events-none" />
                    <span className="font-mono">{p}</span>
                    {sel && <Check className="h-4 w-4 ml-auto text-emerald-500" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
